import React, { useState } from "react";
import { useParams } from "react-router-dom";
import CollectionSidebar from "./components/CollectionSidebar";
import DataCollectionMap from "./components/DataCollectionMap";
import useDataCollection from "./hooks/useDataCollection";
import MonitorRunDialog from "./components/monitoring/MonitorRunDialog";
import MonitoringRunsPanel from "./components/monitoring/MonitoringRunsPanel";
import { startMonitoringRun } from "./services/monitoringService";
import "./DataCollectionPage.css";

export default function DataCollectionPage() {
  const { id = "" } = useParams();
  const store = useDataCollection(id);
  const [selectedPathId, setSelectedPathId] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [draftPoints, setDraftPoints] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");
  const [showMonitorDialog, setShowMonitorDialog] = useState(false);
  const [showRuns, setShowRuns] = useState(false);
  const [isStartingRun, setIsStartingRun] = useState(false);
  const [monitorError, setMonitorError] = useState("");
  const [focusRunId, setFocusRunId] = useState("");

  const startDrawing = () => {
    setSelectedPathId(null);
    setDraftPoints([]);
    setActionError("");
    setIsDrawing(true);
  };

  const cancelDrawing = () => {
    setDraftPoints([]);
    setIsDrawing(false);
  };

  const savePath = async () => {
    if (draftPoints.length < 2) return;
    setIsSaving(true);
    setActionError("");
    try {
      const pathRef = await store.createPath({ name: `Path ${store.paths.length + 1}`, points: draftPoints });
      setSelectedPathId(pathRef.id);
      cancelDrawing();
    } catch (error) {
      console.error("Unable to save path", error);
      setActionError("The path could not be saved. Check the public Firestore write permissions.");
    } finally {
      setIsSaving(false);
    }
  };

  const performAction = async (action) => {
    setActionError("");
    try { await action(); }
    catch (error) {
      console.error("Data collection action failed", error);
      setActionError("That change could not be saved. Please try again.");
    }
  };

  const monitorPaths = async (config) => {
    setIsStartingRun(true);
    setMonitorError("");
    try {
      const result = await startMonitoringRun({ collectionId: id, ...config });
      setFocusRunId(result.run_id || "");
      setShowMonitorDialog(false);
      setShowRuns(true);
      setNotice("Traffic monitoring was scheduled successfully.");
      window.setTimeout(() => setNotice(""), 3500);
    } catch (error) {
      console.error("Unable to start monitoring", error);
      setMonitorError(error.status === 409
        ? "A selected path ID was already used with different coordinates. Create a new path before monitoring again."
        : error.message || "Could not start traffic monitoring.");
    } finally {
      setIsStartingRun(false);
    }
  };

  return (
    <main className="dc-page">
      <CollectionSidebar
        collectionId={id}
        collectionName={store.dataCollection?.name}
        paths={store.paths}
        selectedPathId={selectedPathId}
        isDrawing={isDrawing}
        draftPointCount={draftPoints.length}
        isSaving={isSaving}
        error={store.error || actionError}
        onStartDrawing={startDrawing}
        onUndoPoint={() => setDraftPoints((points) => points.slice(0, -1))}
        onCancelDrawing={cancelDrawing}
        onSavePath={savePath}
        onSelectPath={setSelectedPathId}
        onRenamePath={(pathId, name) => performAction(() => store.renamePath(pathId, name))}
        onDeletePath={(pathId) => performAction(async () => {
          await store.deletePath(pathId);
          if (selectedPathId === pathId) setSelectedPathId(null);
        })}
        onMonitor={() => { setMonitorError(""); setShowMonitorDialog(true); }}
        onViewRuns={() => setShowRuns(true)}
      />
      <section className="dc-map-panel">
        {store.isLoading && <div className="dc-loading">Loading paths…</div>}
        <DataCollectionMap
          paths={store.paths}
          selectedPathId={selectedPathId}
          draftPoints={draftPoints}
          isDrawing={isDrawing}
          onAddPoint={(point) => setDraftPoints((points) => points.length < 25 ? [...points, point] : points)}
        />
        {notice && <div className="dc-toast">{notice}</div>}
        {showRuns && <MonitoringRunsPanel collectionId={id} paths={store.paths} focusRunId={focusRunId} onClose={() => setShowRuns(false)} />}
      </section>
      {showMonitorDialog && <MonitorRunDialog
        paths={store.paths}
        isSubmitting={isStartingRun}
        error={monitorError}
        onClose={() => !isStartingRun && setShowMonitorDialog(false)}
        onSubmit={monitorPaths}
      />}
    </main>
  );
}
